import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, MenuIcon, PlusIcon, XIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { mobileQuickNav } from '../../data/adminNav';
import { buttonClasses } from '../ui/Button';
import { AdminSidebar } from './shell/AdminSidebar';
import { AdminLogo } from './shell/AdminLogo';
import { GlobalSearch } from './shell/GlobalSearch';
import { NotificationsMenu } from './shell/NotificationsMenu';
import { ProfileMenu } from './shell/ProfileMenu';
import { ThemeToggle } from './shell/ThemeToggle';
import { adminThemeClass, useAdminTheme } from '../../hooks/useAdminTheme';
const ease = [0.23, 1, 0.32, 1] as const;
export function AdminShell() {
  const {
    logout,
    dataError,
    rulesError,
    enquiries
  } = useAdminData();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    theme
  } = useAdminTheme();
  const newEnquiries = enquiries.filter(item => item.status === 'Ny henvendelse').length;
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  async function handleLogout() {
    await logout();
    navigate('/admin/login');
  }
  return <div className={`${adminThemeClass(theme)} flex min-h-screen w-full bg-canvas font-sans text-ink`} data-id="element-46" data-mp="35ra09dn3f74u">
      {/* Fast sidebar på desktop */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-line bg-canvas lg:block" data-id="element-47" data-mp="3etsyfkfxovkr">
        <AdminSidebar newEnquiries={newEnquiries} onLogout={handleLogout} data-id="element-48" data-mp="3tife5n9h3pci" />
      </aside>

      {/* Skuffe-menu på mobil */}
      <AnimatePresence data-id="element-49" data-mp="3ojkw4njby4tg">
        {drawerOpen && <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-id="element-50" data-mp="6d6jcrwss0wn">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} data-id="element-51" data-mp="2ce8xvc6vpys" />
            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.26,
          ease
        }} className="absolute inset-y-0 left-0 flex w-[min(19rem,85vw)] flex-col border-r border-line bg-canvas" data-id="element-52" data-mp="jcp45tbb0pba">
              <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Luk menu" className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft hover:bg-white hover:text-ink" data-id="element-53" data-mp="x3lwrk58d6ic">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-54" data-mp="33wnz2m1hztk0" />
              </button>
              <div className="min-h-0 flex-1" data-id="element-55" data-mp="38v7q30uy7vj8">
                <AdminSidebar newEnquiries={newEnquiries} onNavigate={() => setDrawerOpen(false)} onLogout={handleLogout} header={<div className="px-4 pb-2 pt-16" data-id="element-57" data-mp="1h6rit918ujmk">
                      <GlobalSearch onNavigate={() => setDrawerOpen(false)} data-id="element-58" data-mp="1h79mgqq8k04i" />
                    </div>} data-id="element-56" data-mp="38w0uvwackayq" />
              </div>
            </motion.aside>
          </div>}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-59" data-mp="28vl8dxdjd98t">
        <header className="sticky top-0 z-40 border-b border-line bg-canvas/90 backdrop-blur" data-id="element-60" data-mp="24xdmyp84njra">
          <div className="flex h-16 items-center gap-3 px-4 md:px-8" data-id="element-61" data-mp="1zw6j3193v4pw">
            <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Åbn menu" className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft hover:bg-white hover:text-ink lg:hidden" data-id="element-62" data-mp="1uyeofzh2t4i0">
              <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-63" data-mp="27fp6gbso43xy" />
            </button>
            <div className="lg:hidden" data-id="element-64" data-mp="27dulixhecbtv">
              <AdminLogo data-id="element-65" data-mp="2c6nphj51jg4w" />
            </div>

            <div className="hidden max-w-xl flex-1 md:block" data-id="element-66" data-mp="2c8idj6blzod7">
              <GlobalSearch data-id="element-67" data-mp="1yg25ts7rj25y" />
            </div>

            <div className="ml-auto flex items-center gap-1.5 md:gap-3" data-id="element-68" data-mp="1yd69g8bkcljm">
              <ThemeToggle data-id="element-69" data-mp="23ec2k794yato" />
              <NotificationsMenu data-id="element-70" data-mp="2r3olhyu9lx96" />
              <span className="hidden h-6 w-px bg-line md:block" aria-hidden="true" data-id="element-71" data-mp="2r3euq91ebm2f" />
              <ProfileMenu onLogout={handleLogout} data-id="element-72" data-mp="2v5rzau4epdxm" />
              <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md', 'hidden rounded-xl font-semibold sm:inline-flex')} data-id="element-73" data-mp="2v4gtkorehw3v">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-74" data-mp="2m4bowmdldbte" />
                Ny sag
              </Link>
              <Link to="/admin/sager/ny" aria-label="Ny sag" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-white transition-colors duration-150 hover:bg-accent-deep sm:hidden" data-id="element-75" data-mp="15psicqujqk7f">
                <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-76" data-mp="y6sy6iz9gon0" />
              </Link>
            </div>
          </div>
        </header>

        {dataError && <p role="alert" className="flex items-start gap-2 border-b border-danger/30 bg-danger/10 px-5 py-3 text-sm text-danger md:px-8" data-id="element-77" data-mp="1c5sqc0lzflnx">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-78" data-mp="jiy6n5wzoqbi" />
            {dataError}
          </p>}

        {rulesError && <p role="status" className="flex items-start gap-2 border-b border-line bg-accent-soft px-5 py-3 text-sm text-accent-deep md:px-8" data-id="element-79" data-mp="t7pxtbu876ld">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-80" data-mp="oewr4if75o6n" />
            {rulesError}
          </p>}

        <main className="min-w-0 flex-1 pb-24 lg:pb-0" data-id="element-81" data-mp="3ry9iq0ui8h9k">
          <Outlet data-id="element-82" data-mp="3rvncq5n9i9tg" />
        </main>

        {/* Hurtig adgang på mobil */}
        <nav aria-label="Hurtig navigation" className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-line bg-canvas/95 backdrop-blur lg:hidden" data-id="element-83" data-mp="1vtsd4xgew5hm">
          {mobileQuickNav.map(item => <NavLink key={item.to} to={item.to} end={item.end} className={({
          isActive
        }) => `relative flex flex-1 flex-col items-center gap-1 py-3 text-[11px] transition-colors duration-150 ease-smooth ${isActive ? 'text-accent' : 'text-ink-muted'}`} data-id="element-84" data-mp="25mhkbg4xix75">
              <item.icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-85" data-mp="1mslt4eek0cjm" />
              {item.label}
              {item.showEnquiryBadge && newEnquiries > 0 && <span className="absolute right-1/4 top-2 h-2 w-2 rounded-full bg-danger" aria-hidden="true" data-id="element-86" data-mp="2y8aej1c5t3ib" />}
            </NavLink>)}
          <button type="button" onClick={() => setDrawerOpen(true)} className="flex flex-1 flex-col items-center gap-1 py-3 text-[11px] text-ink-muted" data-id="element-87" data-mp="qn29ntiteel6">
            <MenuIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-88" data-mp="huql6d79h17u" />
            Menu
          </button>
        </nav>
      </div>
    </div>;
}